import { useCallback, useState } from 'react';
import * as React from 'react';

import { ApplicationAuthorization, EmployeeTier } from '@/types/schema';
import * as diagnostics from '@/utils/diagnostics';
import { getEnvironment } from '@/utils/environmentUtils';

import { ASSET_INTEGRATION_PROVIDER_DISPLAY_NAMES } from '../assetProviderIntegrations/shared/constants';
import { useAreFeatureFlagsPopulated } from '../featureFlags/useFeatureFlag';
import { useAICapabilitiesEnabled } from '../tenant/TenantDetailsContext/hooks/useAICapabilitiesEnabled';
import { useTenantDetailsContext } from '../tenant/TenantDetailsContext/TenantDetailsContext';
import {
  Authentication_ApplicationAuthorizationFragment,
  Authentication_CollaboratorFragment,
  Authentication_EmployeeFragment,
} from './graphql/Authentication.generated';
import {
  GetUserStateQuery,
  useGetUserStateQuery,
} from './graphql/Authentication.generated';
import {
  AuthorizationStatus,
  BehaviorAuthorizationType,
  private_hasBehaviorAuthorization,
} from './hooks/useHasBehaviorAuthorization';

type HydratedUserState = GetUserStateQuery['currentUser'] & {
  loggedInToAddepar: boolean;
  loggedInToBlackDiamond: boolean;
  loggedInToOrion: boolean;
  loggedInToEMoney: boolean;
  tier: EmployeeTier;
  integrationLoginExpired: boolean;
  apiUserIntegrationLoginExpired: boolean;
  expiredIntegrationNames: string[];
  userExpiredIntegrationNames: string[];
  authorization: Authentication_ApplicationAuthorizationFragment;
  collaborator: Authentication_CollaboratorFragment | null;
  employee: Authentication_EmployeeFragment | null;
  /** IDs of the teams the current user belongs to (deduplicated across employees). */
  teamIds: string[];
};

export type CurrentUserState = HydratedUserState | null;

interface IUserContext {
  currentUser: CurrentUserState;
  __setDevImpersonatedAuthorizationOverride: (
    appAuth: ApplicationAuthorization | null
  ) => void;
  __setDevImpersonatedUserTierOverride: (tier: EmployeeTier) => void;
}

export const UserContext = React.createContext<IUserContext | null>(null);

export const UserProvider = ({
  children,
}: React.PropsWithChildren<Record<string, unknown>>) => {
  const { tier: tenantTier } = useTenantDetailsContext();
  const areFeatureFlagsPopulated = useAreFeatureFlagsPopulated();
  const isAICapabilitiesEnabled = useAICapabilitiesEnabled();
  const [currentUser, setCurrentUser] = useState<CurrentUserState>(null);
  const [authorizationOverride, setAuthorizationOverride] =
    useState<null | ApplicationAuthorization>(null);
  const [userTierOverride, setUserTierOverride] = useState<EmployeeTier | null>(
    null
  );
  const { data, error } = useGetUserStateQuery({
    // this shouldn't need to go to the network, but in a scenario when the cache is fully cleared
    // for some reason, we don't want to prevent it from doing so
    fetchPolicy: 'cache-first',
  });

  const handleSetAuthorizationOverride = useCallback(
    (appAuth: ApplicationAuthorization | null) => {
      if (getEnvironment() === 'prod') {
        throw new Error('This cannot be used in production.');
      }
      setAuthorizationOverride(appAuth);
    },
    [setAuthorizationOverride]
  );

  React.useEffect(() => {
    if (error) {
      setCurrentUser(null);
      return diagnostics.error('Failed to fetch current user', error);
    } else if (data?.currentUser) {
      // User -> Employees is a one-to-many relationship, becuase users can span tenants and will have
      // one employee object per tenants. But there should only be one employee per user in this context,
      // because we're already in a specific tenant.
      const associatedEmployees = data.currentUser.employees ?? [];
      if (associatedEmployees.length > 1) {
        diagnostics.error('Multiple employees found for user', undefined, {
          userId: data.currentUser.id,
        });
      }
      const userEmployee = associatedEmployees[0];
      const userTier = userEmployee?.tier ?? EmployeeTier.Advanced;

      const associatedCollaborators = data.currentUser.collaborators ?? [];
      if (associatedCollaborators.length > 1) {
        diagnostics.error('Multiple collaborators found for user', undefined, {
          userId: data.currentUser.id,
          collaboratorIds: associatedCollaborators.map((c) => c.id).join(','),
        });
      }
      const userCollaborator = associatedCollaborators[0];

      // NOTE: isTokenValid will always be false if the user isn't logged into the integration,
      // hence the need to check both sides of the valiadation scenario
      const userExpiredIntegrations = [
        {
          name: ASSET_INTEGRATION_PROVIDER_DISPLAY_NAMES.ADDEPAR,
          isLoggedIn: data.isUserLoggedInToAddepar.isLoggedIn,
          isTokenValid: data.isUserLoggedInToAddepar.isTokenValid,
        },
        {
          name: ASSET_INTEGRATION_PROVIDER_DISPLAY_NAMES.BLACK_DIAMOND,
          isLoggedIn: data.isUserLoggedInToBlackDiamond.isLoggedIn,
          isTokenValid: data.isUserLoggedInToBlackDiamond.isTokenValid,
        },
        {
          name: ASSET_INTEGRATION_PROVIDER_DISPLAY_NAMES.ORION,
          isLoggedIn: data.isUserLoggedInToOrion.isLoggedIn,
          isTokenValid: data.isUserLoggedInToOrion.isTokenValid,
        },
        {
          name: ASSET_INTEGRATION_PROVIDER_DISPLAY_NAMES.EMONEY,
          isLoggedIn: data.isUserLoggedInToEMoney.isLoggedIn,
          isTokenValid: data.isUserLoggedInToEMoney.isTokenValid,
        },
      ]
        .filter(({ isLoggedIn, isTokenValid }) => isLoggedIn && !isTokenValid)
        .map(({ name }) => name);

      const userIntegrationLoginExpired = userExpiredIntegrations.length > 0;

      // The login that we use to fetch nightly data is different from the one the users uses. But
      // that one can be expired too, so let's show a notice for it if the user is an admin and can
      // do something about it.
      const userCanInteractWithIntegration =
        private_hasBehaviorAuthorization(
          BehaviorAuthorizationType.CAN_ACCESS_INTEGRATIONS_ADMIN_PAGE,
          data.applicationAuthorization,
          userTier,
          tenantTier,
          isAICapabilitiesEnabled
        ) === AuthorizationStatus.ALLOWED;

      const expiredIntegrations = [
        {
          name: ASSET_INTEGRATION_PROVIDER_DISPLAY_NAMES.ADDEPAR,
          isLoggedIn: data.isIntegrationUserLoggedInToAddepar.isLoggedIn,
          isTokenValid: data.isIntegrationUserLoggedInToAddepar.isTokenValid,
        },
        {
          name: ASSET_INTEGRATION_PROVIDER_DISPLAY_NAMES.BLACK_DIAMOND,
          isLoggedIn: data.isIntegrationUserLoggedInToBlackDiamond.isLoggedIn,
          isTokenValid:
            data.isIntegrationUserLoggedInToBlackDiamond.isTokenValid,
        },
        {
          name: ASSET_INTEGRATION_PROVIDER_DISPLAY_NAMES.ORION,
          isLoggedIn: data.isIntegrationUserLoggedInToOrion.isLoggedIn,
          isTokenValid: data.isIntegrationUserLoggedInToOrion.isTokenValid,
        },
        {
          name: ASSET_INTEGRATION_PROVIDER_DISPLAY_NAMES.EMONEY,
          isLoggedIn: data.isIntegrationUserLoggedInToEMoney.isLoggedIn,
          isTokenValid: data.isIntegrationUserLoggedInToEMoney.isTokenValid,
        },
      ]
        .filter(({ isLoggedIn, isTokenValid }) => isLoggedIn && !isTokenValid)
        .map(({ name }) => name);

      const integrationLoginExpired = expiredIntegrations.length > 0;

      // we need to reach directly into this API here because the normal hook path relies
      // on *this* context to be set first
      const apiUserIntegrationLoginExpired =
        integrationLoginExpired && userCanInteractWithIntegration;

      const teamIds = Array.from(
        new Set(
          associatedEmployees.flatMap(
            (emp) => emp.teams?.map((t) => t.id) ?? []
          )
        )
      );

      setCurrentUser({
        ...data.currentUser,
        employee: userEmployee ?? null,
        collaborator: userCollaborator ?? null,
        teamIds,
        loggedInToAddepar: data.isUserLoggedInToAddepar.isLoggedIn,
        loggedInToBlackDiamond: data.isUserLoggedInToBlackDiamond.isLoggedIn,
        loggedInToEMoney: data.isUserLoggedInToEMoney.isLoggedIn,
        loggedInToOrion: data.isUserLoggedInToOrion.isLoggedIn,
        tier: userTierOverride ?? userTier,
        integrationLoginExpired: userIntegrationLoginExpired,
        apiUserIntegrationLoginExpired: apiUserIntegrationLoginExpired,
        expiredIntegrationNames: expiredIntegrations,
        userExpiredIntegrationNames: userExpiredIntegrations,
        authorization: authorizationOverride
          ? authorizationOverride
          : data.applicationAuthorization,
      });
    }
  }, [
    authorizationOverride,
    data,
    error,
    setCurrentUser,
    areFeatureFlagsPopulated,
    currentUser?.tier,
    userTierOverride,
    tenantTier,
    isAICapabilitiesEnabled,
  ]);

  return (
    <UserContext.Provider
      value={{
        currentUser,
        __setDevImpersonatedAuthorizationOverride:
          handleSetAuthorizationOverride,
        __setDevImpersonatedUserTierOverride: setUserTierOverride,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};
